import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '@src/core/hooks';
import { publicApiTokenSelector } from '@src/stores/app';

import type { Order } from '@src/core/buyerApiClient';

import { isPositiveAmount } from '../core/isPositiveAmount';
import { findOrderMatchingSnapshot, type CashbackGrowthOrderSnapshot } from '../core/cashbackGrowthOrderSnapshot';
import {
  dismissCashbackGrowth,
  isCashbackGrowthModalDismissed,
  readCashbackGrowthOrderSnapshot,
  writeCashbackGrowthOrderSnapshot,
} from '../core/cashbackGrowthPersistence';

export type UseCashbackGrowthPersistenceOptions = {
  /** When true, only use persisted snapshot order id if it matches the signed-in buyer's orders. */
  validateSnapshotAgainstOrders?: boolean;
  orders?: Order[];
};

export const useCashbackGrowthPersistence = (
  fallbackOrderId?: string,
  options: UseCashbackGrowthPersistenceOptions = {},
) => {
  const { validateSnapshotAgainstOrders = false, orders } = options;
  const publicApiKey = useAppSelector(publicApiTokenSelector) ?? '';

  const [snapshot, setSnapshot] = useState<CashbackGrowthOrderSnapshot | null>(() =>
    publicApiKey ? readCashbackGrowthOrderSnapshot(publicApiKey) : null,
  );

  const trustPersistedSnapshot = useMemo(() => {
    if (!validateSnapshotAgainstOrders) {
      return true;
    }

    return Boolean(findOrderMatchingSnapshot(orders, snapshot));
  }, [orders, snapshot, validateSnapshotAgainstOrders]);

  const resolvedOrderId = useMemo(() => {
    if (snapshot && isPositiveAmount(snapshot.amountCents) && trustPersistedSnapshot) {
      return snapshot.orderId;
    }

    return fallbackOrderId;
  }, [fallbackOrderId, snapshot, trustPersistedSnapshot]);

  const [modalDismissed, setModalDismissed] = useState(() =>
    resolvedOrderId && publicApiKey ? isCashbackGrowthModalDismissed(publicApiKey, resolvedOrderId) : false,
  );

  const syncModalDismissed = useCallback(
    (nextOrderId: string) => {
      if (!publicApiKey) {
        return;
      }
      setModalDismissed(isCashbackGrowthModalDismissed(publicApiKey, nextOrderId));
    },
    [publicApiKey],
  );

  useEffect(() => {
    if (resolvedOrderId && publicApiKey) {
      syncModalDismissed(resolvedOrderId);
    }
  }, [publicApiKey, resolvedOrderId, syncModalDismissed]);

  const refresh = useCallback(() => {
    if (!publicApiKey) {
      return;
    }
    const nextSnapshot = readCashbackGrowthOrderSnapshot(publicApiKey);
    setSnapshot(nextSnapshot);
    if (nextSnapshot) {
      syncModalDismissed(nextSnapshot.orderId);
    }
  }, [publicApiKey, syncModalDismissed]);

  const persistSnapshot = useCallback(
    (next: CashbackGrowthOrderSnapshot) => {
      if (!publicApiKey) {
        return;
      }
      writeCashbackGrowthOrderSnapshot(publicApiKey, next);
      setSnapshot(next);
      syncModalDismissed(next.orderId);
    },
    [publicApiKey, syncModalDismissed],
  );

  const dismiss = useCallback(
    (next: CashbackGrowthOrderSnapshot) => {
      if (!publicApiKey) {
        return;
      }
      dismissCashbackGrowth(publicApiKey, next);
      setModalDismissed(true);
      setSnapshot(next);
    },
    [publicApiKey],
  );

  return {
    publicApiKey,
    snapshot,
    modalDismissed,
    showBanner: modalDismissed,
    persistSnapshot,
    dismiss,
    refresh,
  };
};
